import { useState } from 'react';
import { Platform, Pressable, StyleSheet, Text, View, type StyleProp, type TextStyle } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, fontFamily } from '~/ui/tokens';
import { convertPriceCeil, formatCurrency, formatExactAmount } from '../lib/currency';
import { useCurrency } from '~/stores/useCurrency';

type Props = { amount: number | string; currencyCode: string; textStyle?: StyleProp<TextStyle> };

// Property-payable taxes are shown exactly as ETG supplies them, in ETG's
// currency. When the guest browses in another currency, an (i) reveals an
// approximate converted figure — an estimate, never the amount charged.
export function TaxAmount({ amount, currencyCode, textStyle }: Props) {
  const { selectedCurrency, rates } = useCurrency();
  const [open, setOpen] = useState(false);
  const exact = formatExactAmount(amount, currencyCode);
  const source = (currencyCode || 'USD').toUpperCase();
  const differs = source !== selectedCurrency.toUpperCase();
  if (!differs) return <Text style={textStyle}>{exact}</Text>;

  const approx = formatCurrency(convertPriceCeil(amount, source, selectedCurrency, rates), selectedCurrency);
  return (
    <View style={styles.wrap}>
      <View style={styles.line}>
        <Text style={textStyle}>{exact}</Text>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`Approximate amount in ${selectedCurrency}`}
          hitSlop={8}
          // Web hover has already opened it by the time the click lands, so a
          // toggle would immediately close it again; only touch toggles.
          onPress={() => setOpen((value) => (Platform.OS === 'web' ? true : !value))}
          onHoverIn={() => setOpen(true)}
          onHoverOut={() => setOpen(false)}
        >
          <Ionicons name="information-circle-outline" size={16} color={colors.inkSoft} />
        </Pressable>
      </View>
      {/* Floats over the row instead of adding a line: extra height shifts the
          bottom-anchored sheet, moving the icon out from under the pointer,
          which hides the hint, which shifts it back — a hover flicker loop. */}
      {open ? (
        <View pointerEvents="none" style={styles.hintBubble}>
          <Text style={styles.hint}>≈ {approx} (estimate)</Text>
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { position: 'relative', alignItems: 'flex-end', zIndex: 1 },
  line: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  hintBubble: { position: 'absolute', top: -2, right: '100%', marginRight: 8, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6, borderWidth: 1, borderColor: colors.lineSoft, backgroundColor: colors.card, zIndex: 10 },
  hint: { fontFamily: fontFamily.regular, fontSize: 12, color: colors.inkSoft, whiteSpace: 'nowrap' } as TextStyle,
});
